import { Injectable } from "@angular/core";
import { Subject } from "rxjs";

import { UserSessionService } from "../../../services/user-session/user-session.service";
import { WebservicesService } from "../../../services/webservices/webservices.service";
import { ConfigService } from "../../../services/config/config.service";
import { CustomerModel } from "../../../models/customer-model";
import { InAppBrowserObject } from "@ionic-native/in-app-browser/ngx";
import {
  GameLaunchParams,
  VendorAdapterInterface,
} from "./vendor-adapter-interface";
import { GameViewInterface, ODRController } from "../../odr-controller";
import { PlatformResponse } from "src/app/interfaces/webservices-interface";

export interface GameRequest {
  name: string;
  gameId: number;
  addressIp: any;
  port: number;
  chipType: string;
  gameTypeId: number;
  numSeats?: number;
  sessionId: any;
}

@Injectable()
export class InternalAdapter implements VendorAdapterInterface {
  protected customer: CustomerModel;
  protected sessionToken: string;
  protected windowRef: InAppBrowserObject;

  constructor(
    private webservices: WebservicesService,
    private userSession: UserSessionService,
    private config: ConfigService,
    private telemetrySubject: Subject<any>
  ) {
    this.userSession.getCustomer().subscribe((customer) => {
      this.customer = customer;
      this.sessionToken = this.userSession.getSessionToken();
    });
  }

  public setWindowRef(windowRef: InAppBrowserObject | GameViewInterface): void {
    this.windowRef = windowRef as InAppBrowserObject;
  }

  public getWindowRef(): InAppBrowserObject | GameViewInterface {
    return this.windowRef;
  }

  public odrSessionId(): Subject<any> {
    return this.telemetrySubject;
  }

  public windowStrategy() {
    return "inappbrowser";
  }

  public shouldDescendIntoDirs() {
    return false;
  }

  public getTagLayout(extGameParams: GameLaunchParams): Promise<Array<any>> {
    return this.config.isLoaded().then(() => {
      let jurisdiction = ODRController.clientAppJurisdiction(this.config.getClientApp());
      let tag = `internal-${jurisdiction}-${extGameParams.extGameType}`;
      return [{ loc: "/", tag: tag }];
    })
  }

  public credentials(p: any): Promise<any> {
    return new Promise((resolve, reject) => {
      var params: any = {
        customer_id: p.customer_id ? p.customer_id : null,
        session_token: p.session_token ? p.session_token : null,
        vendor: "internal",
        game_launcher_id: p.gameLaunchId,
        clientapp: this.config.getClientApp(),
      };

      this.webservices.get("games/get-vendor-credentials", params).subscribe(
        (resp: PlatformResponse<any>) => {
          if ("undefined" !== resp.payload.gameRequest) {
            this.telemetrySubject.next(resp.payload.gamerequest.gameId);
            resolve(Object.assign(p, resp.payload));
          } else {
            console.log(
              "internal: games/get-vendor-credentials: gamerequest failed:",
              resp
            );
            reject(resp);
          }
        },
        (err: any) => {
          console.log("internal: games/get-vendor-credentials error: ", err);
          reject(err);
        }
      );
    });
  }

  public launchODR(windowRef: InAppBrowserObject, gamePrms: any) {
    this.windowRef = windowRef;
    let origHtml5Baseurl = gamePrms.config.HTML5_BASEURL;
    gamePrms.config.HTML5_BASEURL = "http://localhost:9090";
    let gameRequest: GameRequest = gamePrms.gamerequest;
    if (!gamePrms.gamerequest.sessionId) {
      gamePrms.gamerequest.sessionId = "DEMO-PLAYER";
    }

    gamePrms.gamerequest.extra_launch_data = gamePrms.ODR.extra_launch_data != null ? gamePrms.ODR.extra_launch_data : null;
    console.log("launchODR", gamePrms);

    gamePrms.gamerequest.game_metadata = JSON.stringify({ clientapp: this.config.getClientApp() })

    let strGameRequest: string = JSON.stringify(gamePrms.gamerequest);
    let strConfig: string = JSON.stringify(gamePrms.config);
    let strSettings: string = JSON.stringify(gamePrms.settings);
    let strWebOrMobile: string = "";

    let extraLaunchData;
    if (gamePrms.gamerequest.extra_launch_data != null) {
      extraLaunchData = JSON.parse(gamePrms.gamerequest.extra_launch_data);
    } else {
      extraLaunchData = gamePrms.extra_launch_data != null ? JSON.parse(gamePrms.extra_launch_data) : null;
    }
    if (extraLaunchData != null && extraLaunchData.hasMobileSkin != null)
      strWebOrMobile = extraLaunchData.hasMobileSkin == "1" ? "/mobile" : "/web";
    else
      strWebOrMobile = gameRequest.gameTypeId == 334 ? "/mobile" : "/web";

    windowRef
      .executeScript({
        file: "https://cdnjs.cloudflare.com/ajax/libs/babel-core/5.6.15/browser-polyfill.min.js",
      })
      .then(() =>
        windowRef
          .executeScript({
            file:
              "http://localhost:9090/games/" +
              gameRequest.gameTypeId +
              strWebOrMobile +
              "/game.js",
          })
          .then(() =>
            windowRef
              .executeScript({ file: origHtml5Baseurl + "/rungame.js" })
              .then(() =>
                windowRef.executeScript({
                  code: `document.body.style.fontSize = '24px'
        
                runGame( ${strGameRequest}, ${strConfig}, ${strSettings}, ${gamePrms.config.FEATURE_GAME_RESIZE_STRATEGY});`,
                })
              )
          )
      );
    return windowRef;
  }

  public evaluateJavascript(js: string): Promise<any> {
    if (this.windowRef) {
      return this.windowRef.executeScript({
        code: js,
      });
    } else {
      return Promise.reject("ODR: everi.ts: invalid windowRef");
    }
  }

  public onGameClosed(windowRef: InAppBrowserObject, closedCb: () => void) {
    var ticket: number = 1; // prevent closeCb from being called by multiple close events

    let intervalID = setInterval(() => {
      windowRef
        .executeScript({ code: "window._game_has_closed" })
        .then((val) => {
          if (val[0]) {
            clearInterval(intervalID);
            if (!--ticket) {
              console.log("ODR: internal.ts: onGameClosed: _game_has_closed");
              windowRef.close();
              closedCb();
            } else {
              ticket++;
            }
          }
        });
    }, 500);
    windowRef.on("exit").subscribe((event) => {
      if (!--ticket) {
        console.log("ODR: internal.ts: onGameClosed: exit event");
        closedCb();
      } else {
        ticket++;
      }
    });
  }
}
